import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { v4 as uuidv4 } from 'uuid'

import { Observable, of, tap } from 'rxjs'

import { ActionApi } from '@flowx/designer/shared/api/action.api'
import { EventHandlerApiService } from '@flowx/designer/shared/api/event-handler.api'
import { NodeApiService } from '@flowx/designer/shared/api/node.api'
import {
  ApplicationType,
  CurrencyFormat,
  DateFormat,
  ProxyApiAction,
  RenderingPlatform,
  transformApplicationPlatformInProcessPlatform,
} from '@flowx/designer/shared/entities/application.entity'
import { ApplicationsService } from '@flowx/designer/shared/services/applications.service'
import { ProcessDefinitionService } from '@flowx/designer/shared/services/process-definition.service'
import { selectWorkspaceId } from '@flowx/designer/shared/store/selectors/router.selectors'

import { AgentBuilderApi } from '../../agent-builder/services/agent-builder.api'
import { SWIMLANE_DEFAULT_LAYOUT_OPTIONS } from '../../bpmn-editor/bpmn-graph.constants'
import { ActionDefinition, NodeValue } from '../../core/dictionary/process.dictionary'

const EMPTY_AGENT_TEMPLATE_DEFINITION = {
  nodes: [],
  edges: [],
  viewport: {
    x: 0,
    y: 0,
    zoom: 1,
  },
}
@Injectable({ providedIn: 'root' })
/* eslint-disable @typescript-eslint/member-ordering */
export class ProjectActionsService {
  #agentBuilderApiService = inject(AgentBuilderApi)
  #applicationsService = inject(ApplicationsService)
  #processDefinitionService = inject(ProcessDefinitionService)
  #actionApi = inject(ActionApi)
  #nodeApiService = inject(NodeApiService)
  #uiActionApiService = inject(EventHandlerApiService)
  #router = inject(Router)
  #store = inject(Store)
  #workspaceId = this.#store.selectSignal(selectWorkspaceId)

  #projectBasePath = (appId: string, appVersionId: string): string[] => [
    'guard',
    'workspace',
    this.#workspaceId(),
    'projects',
    appId,
    'config',
    appVersionId,
  ]

  parse_actions_to_run = (
    action_name: string,
    action_paramenters: any,
    appId: string,
    appVersionId: string
  ): Observable<any> => {
    const timestamp = Date.now()
    switch (action_name) {
      case 'create_project': {
        const concatName = action_paramenters.project_name
          ? `${action_paramenters.project_name}_${timestamp}`
          : `${timestamp}`
        return this.create_project_prompt(concatName)
      }
      case 'create_process': {
        const concatName = action_paramenters.process_name
          ? `${action_paramenters.process_name}_${timestamp}`
          : `${timestamp}`
        return this.create_process_prompt(concatName, appId, appVersionId)
      }
      case 'create_agent': {
        const concatName = action_paramenters.agent_code
          ? `${action_paramenters.agent_code}_${timestamp}`
          : `${timestamp}`
        return this.create_agent(
          action_paramenters.agent_name,
          concatName,
          action_paramenters.agent_description,
          appId,
          appVersionId
        )
      }
      default:
        return of(null)
    }
  }

  create_send_message_action_to_agent = (action_paramenters: any): Observable<any> =>
    this.#nodeApiService.createAction(action_paramenters.nodeDefinitionId, {
      ...action_paramenters,
    } as any)

  create_receive_message_action_to_agent = (
    nodeDefinitionId: number,
    values: NodeValue[]
  ): Observable<any> => this.#nodeApiService.createValues(nodeDefinitionId, values)

  create_business_rule_upload_action = (action_paramenters: any): Observable<any> =>
    this.#nodeApiService.createAction(action_paramenters.nodeDefinitionId, {
      ...action_paramenters,
    } as any)

  update_business_rule_upload_action = (action_paramenters: ActionDefinition): Observable<any> =>
    this.#actionApi.updateAction(action_paramenters)

  create_ui_receive_message_action_from_document_upload = (
    nodeDefinitionId: number,
    values: NodeValue[]
  ): Observable<any> => this.#nodeApiService.createValues(nodeDefinitionId, values)

  create_ui_template_action = (action_paramenters: any): Observable<any> =>
    this.#uiActionApiService.addEventHandler(
      action_paramenters.templateConfigId,
      { ...action_paramenters } as any,
      undefined
    )

  get_template_config_id = (areaId: number): Observable<any> =>
    this.#nodeApiService.getUserTaskTemplateConfigs([areaId])

  get_node_actions = (nodeId: number): Observable<any> =>
    this.#nodeApiService.getNodeActions(nodeId)

  create_agent = (
    agentName: string,
    agentCode: string,
    agentDescription: string,
    appId: string,
    appVersionId: string
  ): Observable<any> =>
    this.#agentBuilderApiService
      .createAgent({
        name: agentName,
        code: agentCode,
        description: agentDescription,
        application_id: appId,
        app_version_id: appVersionId,
        definition: EMPTY_AGENT_TEMPLATE_DEFINITION as any,
        use_memory: false,
        inputs: [
          {
            id: uuidv4(),
            name: 'inputData',
            code: 'input_data',
            type: 'STRING',
            description: 'Default input data for an agent',
            dir: 'input',
          },
        ],
        outputs: [
          {
            id: uuidv4(),
            name: 'outputData',
            code: 'output_data',
            type: 'STRING',
            description: 'Default output data for an agent',
            dir: 'output',
          },
        ],
        type: 'business',
      })
      .pipe(
        tap((response) => {
          this.#router.navigate([
            ...this.#projectBasePath(appId, appVersionId),
            'agent-builder',
            'agents',
            response.id,
            'editor',
          ])
        })
      )

  create_project_prompt = (projectName: string): Observable<any> =>
    this.#applicationsService
      .createApplication({
        name: projectName,
        type: ApplicationType.APP,
        applicationSettings: {
          platforms: RenderingPlatform.OMNICHANNEL,
          entrypoints: {} as any,
          defaultLocaleCode: 'en',
          languages: [{ name: 'English', code: 'en', isDefault: true }],
          themeId: '12345678-1234-1234-1234-123456789012',
          dateDisplayFormat: { format: DateFormat.SHORT },
          numberDisplayFormat: { minDecimals: 1, maxDecimals: 2 },
          currencyDisplayFormat: { format: CurrencyFormat.ISO_CODE },
        },
      })
      .pipe(
        tap((response) => {
          this.#router.navigate([
            ...this.#projectBasePath(response.id, response.mainFirstCommitId),
            'processes',
          ])
        })
      )

  create_process_prompt = (
    processName: string,
    appId: string,
    appVersionId: string
  ): Observable<any> =>
    this.#processDefinitionService
      .createProcess({
        resourceName: processName,
        action: ProxyApiAction.CREATE,
        payload: {
          name: processName,
          appVersionId,
          defaultSwimlaneLayoutOptions: SWIMLANE_DEFAULT_LAYOUT_OPTIONS,
          availableOnPlatform: transformApplicationPlatformInProcessPlatform(
            RenderingPlatform.OMNICHANNEL
          ),
        },
      })
      .pipe(
        tap((response) => {
          this.#router.navigate([
            ...this.#projectBasePath(appId, appVersionId),
            'processes',
            response.resourceId,
            'diagram',
          ])
        })
      )
}
